import type { EntityData, EntitySnapshot } from "@models/data/entityData";
import type { ProjectModel } from "./ProjectModel";

/**
 * `ProjectModelEntities` — partial of {@link ProjectModel} covering entity
 * lookups. Mirrors `Models/ProjectModel/ProjectModel_Entities.cs`.
 *
 * The class is mixed into `ProjectModel` via `applyMixins`; methods access
 * the host class's protected `m_entitiesByScene` map through `this`.
 */
export class ProjectModelEntities {
  hasEntities(this: ProjectModel, sceneId: string): boolean {
    const list = (this as unknown as ProjectModelInternals).m_entitiesByScene.get(sceneId);
    return !!list && list.length > 0;
  }

  getEntities(this: ProjectModel, sceneId: string): ReadonlyArray<EntityData> {
    return (this as unknown as ProjectModelInternals).m_entitiesByScene.get(sceneId) ?? [];
  }

  getEntityById(
    this: ProjectModel,
    sceneId: string,
    entityId: string,
  ): EntityData | null {
    const list = (this as unknown as ProjectModelInternals).m_entitiesByScene.get(sceneId);
    return list?.find((e) => e.id === entityId) ?? null;
  }

  getEntityBySnapshotId(
    this: ProjectModel,
    sceneId: string,
    snapshotId: string,
  ): EntityData | null {
    const list = (this as unknown as ProjectModelInternals).m_entitiesByScene.get(sceneId);
    if (!list) return null;
    for (const entity of list) {
      if (findSnapshot(entity.snapshots, snapshotId)) return entity;
    }
    return null;
  }

  getEntitySnapshotById(
    this: ProjectModel,
    sceneId: string,
    snapshotId: string,
  ): EntitySnapshot | null {
    const list = (this as unknown as ProjectModelInternals).m_entitiesByScene.get(sceneId);
    if (!list) return null;
    for (const entity of list) {
      const snapshot = findSnapshot(entity.snapshots, snapshotId);
      if (snapshot) return snapshot;
    }
    return null;
  }
}

function findSnapshot(
  snapshots: ReadonlyArray<EntitySnapshot>,
  id: string,
): EntitySnapshot | null {
  for (const snap of snapshots) {
    if (snap.id === id) return snap;
    const child = findSnapshot(snap.children, id);
    if (child) return child;
  }
  return null;
}

/** Narrow internals view used by the mixin without exposing the same to
 *  external callers. Kept colocated so we don't leak field shape. */
interface ProjectModelInternals {
  readonly m_entitiesByScene: Map<string, EntityData[]>;
}
